/* eslint-disable import/prefer-default-export */
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { AGENT_ID_QUERY_KEY } from '~/features/agents/constants'
import { sessionStore, store } from '~/store'

export const useSetAgentId = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const setAgentInStore = sessionStore((state) => state.setAgent)
  const setUser = store((state) => state.setUser)

  return useCallback(
    ({
      agentId,
      isImpersonating,
    }: {
      agentId?: string | null
      isImpersonating?: boolean
    }) => {
      // Update search params
      const currentAgentId = searchParams.get(AGENT_ID_QUERY_KEY) || null
      const newAgentId = agentId || null

      if (newAgentId !== currentAgentId) {
        // Functional form so this always merges onto whatever the URL
        // actually is at apply time, not the `searchParams` snapshot closed
        // over when this callback was created. useSetAgentId is called from
        // several app-root-level effects (agent resolution after the agents
        // list loads, auth listeners, sign-out) that can fire asynchronously
        // well after a deep link (e.g. ?conversationId=&messageId=) was
        // added to the URL — building from a stale snapshot here would
        // silently strip those unrelated params out from under it.
        setSearchParams(
          (prev) => {
            const newSearchParams = new URLSearchParams(prev)

            if (newAgentId) {
              newSearchParams.set(AGENT_ID_QUERY_KEY, newAgentId)
            } else {
              newSearchParams.delete(AGENT_ID_QUERY_KEY)
            }

            return newSearchParams
          },
          { replace: true },
        )
      }

      // Update store
      const id = agentId || ''
      setAgentInStore({ id })

      if (id && !isImpersonating) {
        setUser({ lastSelectedAgentId: id })
      }
    },
    [searchParams, setAgentInStore, setSearchParams, setUser],
  )
}
